<template>
  <main class="l-main p-dashboard">
    <TitleMain icon="dashboard" faq="">
      <span class="title">ダッシュボード</span>
    </TitleMain>
    <div class="l-container">
      <AiWritingBanner :projectId="project.id" />
      <div class="l-toolBlock">
        <ButtonToProjects />
        <Breadcrumb />
      </div>

      <Actions :project="project" />

      <ul class="c-tab--balloon l-mb-4">
        <li class="c-tab--balloon__item">
          <Button
            class="c-tab--balloon__link"
            :class="{ 'is-active': selectedDashboard === 'newContent' }"
            @click="changeDashboard('newContent')"
          >
            新規コンテンツ作成のダッシュボード
          </Button>
        </li>
        <li class="c-tab--balloon__item">
          <Button
            class="c-tab--balloon__link"
            :class="{ 'is-active': selectedDashboard === 'refineContent' }"
            @click="changeDashboard('refineContent')"
          >
            既存コンテンツ改善のダッシュボード
          </Button>
        </li>
      </ul>

      <!-- 新規コンテンツ作成 -->
      <section
        v-if="selectedDashboard === 'newContent'"
        class="l-block u-mb20"
        ref="block"
      >
        <div class="l-block__header">
          <TitleSub icon="dashboard" level="h2">
            <span class="title">
              新規コンテンツ作成のダッシュボード
            </span>
          </TitleSub>
        </div>
        <NewContentDashboard
          :tasks="tasksNewContentDone"
          :isLoadingTasksNewContent="isLoadingTasksNewContent"
          :rankedContentsSummary="items.rankedContentsSummary"
          :isLoadingRankedContentsSummary="isLoadingRankedContentsSummary"
        />
      </section>

      <section
        v-if="selectedDashboard === 'newContent'"
        class="l-block"
        ref="block"
      >
        <div class="l-block__header">
          <TitleSub icon="tasklist" level="h2">
            <span class="title">
              新規コンテンツ作成の流れ
            </span>
          </TitleSub>
        </div>
        <NewContent
          :project="project"
          :tasks="items.tasksNewContent"
          :isLoading="isLoadingTasksNewContent"
          @completeTask="completeTask"
        />
      </section>

      <!-- 既存コンテンツ改善 -->
      <section
        v-if="selectedDashboard === 'refineContent'"
        class="l-block u-mb20"
        ref="block"
      >
        <div class="l-block__header">
          <TitleSub icon="dashboard" level="h2">
            <span class="title">
              既存コンテンツ改善のダッシュボード
            </span>
          </TitleSub>
        </div>
        <RefineContentDashboard
          :project="project"
          :existingContentsSummary="items.existingContentsSummary"
          :existingContentsTerm="items.existingContentsTerm"
          :isLoadingExistingContentsSummary="isLoadingExistingContentsSummary"
          :isLoadingExistingContentsTerm="isLoadingExistingContentsTerm"
        />
      </section>

      <section
        v-if="selectedDashboard === 'refineContent'"
        class="l-block"
        ref="block"
      >
        <div class="l-block__header">
          <TitleSub icon="tasklist" level="h2">
            <span class="title">
              既存コンテンツ改善の流れ
            </span>
          </TitleSub>
        </div>
        <RefineContent
          :project="project"
          :tasks="items.tasksRefineContent"
          :isLoading="isLoadingTasksRefineContent"
          @completeRefineTask="completeRefineTask"
        />
      </section>
    </div>
  </main>
</template>

<script>
import Actions from '@/components/dashboard/actions/Actions';
import AiWritingBanner from '@/components/aiWriting/AiWritingBanner';
import Breadcrumb from '@/components/Breadcrumb';
import ButtonToProjects from '@/components/ButtonToProjects';
import TitleMain from '@/components/TitleMain';
import TitleSub from '@/components/TitleSub';
import NewContent from '@/components/dashboard/NewContent';
import NewContentDashboard from '@/components/dashboard/NewContentDashboard';
import RefineContent from '@/components/dashboard/RefineContent';
import RefineContentDashboard from '@/components/dashboard/RefineContentDashboard';
import apiProjects from '@/api/projects';

export default {
  props: ['project'],

  data() {
    return {
      selectedDashboard: this.$store.state.page.dashboard.type,
      isLoadingTasksNewContent: true,
      isLoadingTasksRefineContent: true,
      isLoadingExistingContentsSummary: true,
      isLoadingExistingContentsTerm: true,
      isLoadingRankedContentsSummary: true,
      items: {
        tasksNewContent: [],
        tasksRefineContent: [],
        existingContentsSummary: {
          ctrHigh: {
            contentsCount: 0
          },
          ctrLow: {
            contentsCount: 0
          },
          rankMiddle: {
            contentsCount: 0
          },
          rankLow: {
            contentsCount: 0
          },
          isEmpty: false
        },
        existingContentsTerm: [],
        rankedContentsSummary: {
          rankedContentsCount: 0
        }
      },
      startTime: 0
    };
  },

  components: {
    Actions,
    AiWritingBanner,
    Breadcrumb,
    ButtonToProjects,
    TitleMain,
    TitleSub,
    NewContent,
    NewContentDashboard,
    RefineContent,
    RefineContentDashboard
  },

  created() {
    this.getTasksNewContent();
    this.getTasksRefineContent();
    // 描画されたタイミングをミリ秒で記録
    this.startTime = Date.now();

    this.getExistingContentsSummary();
    this.getExistingContentsTerm();
    this.getRankedContentsSummary();
  },

  mounted() {},

  beforeDestroy() {
    if (process.env.VUE_APP_ENV === 'demo') {
      return;
    }
    const endTime = Date.now();
    // 滞在時間を秒単位でカウント
    const duration = (endTime - this.startTime) / 1000;
    const properties = {
      duration: duration,
      selectedDashboard: this.selectedDashboard
    };
    amplitude
      .getInstance()
      .logEvent('Duration of Visits - Dashboard', properties);
  },

  computed: {
    totalTaskCount() {
      return this.$store.state.project.task.count;
    },

    tasksNewContentDone() {
      return this.items.tasksNewContent.filter(v => {
        return v.column === 'done';
      });
    }
  },

  methods: {
    completeTask(task) {
      const index = this.items.tasksNewContent.findIndex(
        item => item.id === task.id
      );
      //完了させたタスクを配列から削除する
      this.items.tasksNewContent.splice(index, 1);
      // サイドバーのタスクの個数を1減らす
      this.$store.dispatch('project/setTaskCount', this.totalTaskCount - 1);
    },

    completeRefineTask(task) {
      const index = this.items.tasksRefineContent.findIndex(
        item => item.id === task.id
      );
      //完了させたタスクを配列から削除する
      this.items.tasksRefineContent.splice(index, 1);
      // サイドバーのタスクの個数を1減らす
      this.$store.dispatch('project/setTaskCount', this.totalTaskCount - 1);
    },

    getTasksNewContent() {
      this.isLoadingTasksNewContent = true;

      apiProjects.tasks
        .get(
          {
            columns: 'todo,doing,done',
            order: 'createdDate:asc',
            types: 'createContent'
          },
          this.auth.idToken,
          this.project.id
        )
        .then(response => {
          this.items.tasksNewContent = response.data.items;
          this.isLoadingTasksNewContent = false;
        })
        .catch(error => {
          this.isLoadingTasksNewContent = false;
        });
    },

    getTasksRefineContent() {
      this.isLoadingTasksRefineContent = true;
      apiProjects.tasks
        .get(
          {
            columns: 'todo,doing,done',
            order: 'createdDate:asc',
            types: 'rewrite,changeTd'
          },
          this.auth.idToken,
          this.project.id
        )
        .then(response => {
          this.items.tasksRefineContent = response.data.items;
          this.isLoadingTasksRefineContent = false;
        })
        .catch(error => {
          this.isLoadingTasksRefineContent = false;
        });
    },

    getRankedContentsSummary() {
      this.isLoadingRankedContentsSummary = true;
      apiProjects.rankedContentSummary
        .get(this.auth.idToken, this.project.id)
        .then(response => {
          if (response.data) {
            this.items.rankedContentsSummary = response.data;
          }
          this.isLoadingRankedContentsSummary = false;
        })
        .catch(error => {
          this.isLoadingRankedContentsSummary = false;
        });
    },

    changeDashboard(value) {
      if (this.selectedDashboard != value) {
        this.selectedDashboard = value;
        this.saveDashboardType(value);
      }
    },
    saveDashboardType(value) {
      this.$store.dispatch('page/setStatus', {
        page: 'dashboard',
        status: { type: value }
      });
    },

    // ダッシュボードグラフデータ
    getExistingContentsSummary() {
      this.isLoadingExistingContentsSummary = true;

      apiProjects.existingContentsSummary
        .get(this.auth.idToken, this.project.id)
        .then(response => {
          if (response.data) {
            this.items.existingContentsSummary = response.data;
          } else {
            this.items.existingContentsSummary.isEmpty = true;
          }
          this.isLoadingExistingContentsSummary = false;
        })
        .catch(error => {
          this.isLoadingExistingContentsSummary = false;
        });
    },

    getExistingContentsTerm() {
      this.isLoadingExistingContentsTerm = true;
      apiProjects.existingContentsTerm
        .get(this.auth.idToken, this.project.id)
        .then(response => {
          if (response.data.items) {
            this.items.existingContentsTerm = response.data.items;
          }
          this.isLoadingExistingContentsTerm = false;
        })
        .catch(error => {
          this.isLoadingExistingContentsTerm = false;
        });
    }
  }
};
</script>
